(() => {
  const NAME = "cart-upsell";

  if (customElements.get(NAME)) return;

  const CAROUSEL_SELECTOR = ".cart-upsell-carousel";
  const SKELETON_CLASS = "cart-upsell-carousel--skeleton";
  const RECENTLY_VIEWED_KEY = "roka:recently-viewed";

  // The header drawer loads this script before the cart section loads
  // assets/recently-viewed-tracker.js, so on a fresh page load the element
  // upgrades before window.RokaRecentlyViewed exists. Read the store itself
  // (same key, same shape) when the reader is not there yet.
  const readRecentlyViewed = () => {
    if (typeof window.RokaRecentlyViewed?.read === "function") {
      return window.RokaRecentlyViewed.read();
    }

    try {
      const parsed = JSON.parse(
        window.localStorage.getItem(RECENTLY_VIEWED_KEY) || "[]",
      );
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  class CartUpsell extends HTMLElement {
    #abortController = null;
    #resizeObserver = null;

    /**
     * Both the cart page and the drawer re-render themselves wholesale after a
     * change, which would swap a loaded carousel for a fresh shell and fetch
     * the same cards again — a visible blink. When the incoming shell asks for
     * the same URL and the current one holds real cards, the live node is
     * moved across instead. Static so both hosts call the one implementation.
     */
    static adoptLive(currentRoot, incomingRoot) {
      const current = currentRoot?.querySelector(NAME);
      const incoming = incomingRoot?.querySelector(NAME);

      if (!current || !incoming) return false;
      if (current.dataset.seed !== incoming.dataset.seed) return false;
      if (
        !current.querySelector(
          CAROUSEL_SELECTOR + ":not(." + SKELETON_CLASS + ")",
        )
      ) {
        return false;
      }

      // A changed request (a different first item) refreshes behind the live
      // cards rather than replacing them with a skeleton — and keeps them if
      // the new request returns nothing.
      if (current.dataset.url !== incoming.dataset.url) {
        current.dataset.url = incoming.dataset.url;
        current.dataset.stale = "";
      }

      incoming.replaceWith(current);
      return true;
    }

    connectedCallback() {
      this.#abortController = new AbortController();

      // A live carousel moved across a drawer refresh only needs its
      // listeners back; a skeleton means the cards are still to come.
      if (
        this.querySelector(CAROUSEL_SELECTOR + ":not(." + SKELETON_CLASS + ")")
      ) {
        this.#bind();

        if ("stale" in this.dataset) {
          delete this.dataset.stale;
          this.#load({ keepOnFailure: true });
        }

        return;
      }

      // Fetched on connect rather than on approach: the drawer's copy lands
      // while the dialog is closed, where an IntersectionObserver would never
      // report it as intersecting.
      this.#load();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
      this.#resizeObserver?.disconnect();
      this.#resizeObserver = null;
    }

    /**
     * The empty bag lists the products the shopper looked at most recently
     * (assets/recently-viewed-tracker.js writes them on every product page):
     * one card per product, each rendered by sections/cart-recently-viewed-card
     * on that product's URL, dropped into the carousel chrome the host left in
     * a <template>. Nothing viewed, or nothing that renders, means no block.
     */
    async #loadRecentlyViewed() {
      const shell = this.querySelector("template[data-cart-upsell-shell]");
      const entries = readRecentlyViewed();
      const limit = Number.parseInt(this.dataset.limit || "8", 10) || 8;
      const sectionId = this.dataset.cardSection || "cart-recently-viewed-card";
      const handles = entries
        .map((entry) => entry?.handle)
        .filter(Boolean)
        .slice(0, limit);

      if (!shell || handles.length === 0) {
        this.#clear();
        return;
      }

      const signal = this.#abortController?.signal;
      const fetchSlide = async (handle) => {
        const url =
          "/products/" +
          encodeURIComponent(handle) +
          "?sections=" +
          encodeURIComponent(sectionId);
        const response = await fetch(url, { signal });
        if (!response.ok) return null;

        const data = await response.json();
        const markup = data?.[sectionId];
        if (!markup) return null;

        return new DOMParser()
          .parseFromString(markup, "text/html")
          .querySelector(".cart-upsell-carousel__slide");
      };

      try {
        const slides = await Promise.all(
          handles.map((handle) =>
            fetchSlide(handle).catch((error) => {
              if (error?.name === "AbortError") throw error;
              return null;
            }),
          ),
        );
        const cards = slides.filter(Boolean);

        if (cards.length === 0) {
          this.#clear();
          return;
        }

        const carousel = shell.content.firstElementChild.cloneNode(true);
        carousel.querySelector("[data-cart-upsell-track]")?.append(...cards);
        this.replaceChildren(carousel);
        this.#bind();
      } catch (error) {
        if (error?.name === "AbortError") return;
        this.#clear();
      }
    }

    async #load({ keepOnFailure = false } = {}) {
      if (this.dataset.seed === "recently-viewed") {
        await this.#loadRecentlyViewed();
        return;
      }

      // Nothing new to show: drop the skeleton so the host can hide the block,
      // or keep the cards already on screen when this was a refresh.
      const giveUp = () => {
        if (!keepOnFailure) this.#clear();
      };

      const url = this.dataset.url;
      if (!url) {
        giveUp();
        return;
      }

      try {
        const response = await fetch(url, {
          signal: this.#abortController?.signal,
        });

        if (!response.ok) {
          giveUp();
          return;
        }

        const markup = await response.text();
        const parsed = new DOMParser().parseFromString(markup, "text/html");
        const carousel = parsed.querySelector(CAROUSEL_SELECTOR);

        // No recommendations for this cart is a normal outcome, not an error.
        if (!carousel) {
          giveUp();
          return;
        }

        this.replaceChildren(carousel);
        this.#bind();
      } catch (error) {
        // A disconnect mid-fetch is not a failure. Recommendations are
        // additive either way.
        if (error?.name === "AbortError") return;
        giveUp();
      }
    }

    #clear() {
      this.replaceChildren();
    }

    /**
     * A product added to the bag leaves the carousel rather than sitting in
     * it disabled: the recommendations request is seeded from the oldest
     * line, so an add changes neither the URL nor the card set, and the
     * adopted carousel would keep the spent card forever. Hosts call this
     * after /cart/add.js succeeds.
     */
    dropCard(button) {
      const slide = button?.closest(".cart-upsell-carousel__slide");
      if (!slide || !this.contains(slide)) return;

      slide.remove();

      const track = this.querySelector("[data-cart-upsell-track]");
      if (!track?.querySelector(".cart-upsell-carousel__slide")) {
        this.#clear();
        return;
      }

      this.#syncNav(track);
    }

    /**
     * Size selection lives here rather than in a host: it only rewrites the
     * card's own DOM, and the carousel now renders in the cart page and the
     * cart drawer. Adding to the bag still belongs to the host, which owns how
     * it re-renders itself.
     */
    #selectSize(button) {
      if (button.disabled) return;

      const card = button.closest("[data-cart-upsell-card]");
      const variantId = button.dataset.variantId;
      if (!card || !variantId) return;

      for (const option of card.querySelectorAll("[data-cart-upsell-size]")) {
        option.setAttribute("aria-pressed", String(option === button));
      }

      const add = card.querySelector("[data-cart-upsell-add]");
      if (!add) return;

      add.dataset.variantId = variantId;
      add.classList.add("cart-upsell-card__add--ready");
    }

    #bind() {
      const signal = this.#abortController?.signal;
      if (!signal) return;

      this.addEventListener(
        "click",
        (event) => {
          const size = event.target.closest("[data-cart-upsell-size]");
          if (size) this.#selectSize(size);
        },
        { signal },
      );

      const track = this.querySelector("[data-cart-upsell-track]");
      if (!track) return;

      this.querySelector("[data-cart-upsell-prev]")?.addEventListener(
        "click",
        () => this.#scrollTrack(track, -1),
        { signal },
      );

      this.querySelector("[data-cart-upsell-next]")?.addEventListener(
        "click",
        () => this.#scrollTrack(track, 1),
        { signal },
      );

      track.addEventListener("scroll", () => this.#syncNav(track), {
        passive: true,
        signal,
      });

      /*
       * In the cart drawer the carousel lands while the dialog is still closed,
       * so the track has no layout at all: clientWidth and scrollWidth are both
       * 0, which reads as "no overflow" and disabled BOTH arrows for good. Watch
       * the track instead of measuring it once — the first real size arrives
       * when the drawer opens.
       */
      if (typeof ResizeObserver === "function") {
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = new ResizeObserver(() => this.#syncNav(track));
        this.#resizeObserver.observe(track);
      }

      this.#syncNav(track);
    }

    #slideStep(track) {
      const slide = track.querySelector(".cart-upsell-carousel__slide");
      if (!slide) return Math.max(track.clientWidth * 0.8, 240);

      const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    #scrollTrack(track, direction) {
      track.scrollBy({ left: this.#slideStep(track) * direction });
    }

    #syncNav(track) {
      // No layout yet — measuring now would report no overflow and disable
      // both arrows. Leave them alone until the observer reports a real size.
      if (!track.clientWidth) return;

      const maxScroll = track.scrollWidth - track.clientWidth;
      const atStart = track.scrollLeft <= 1;
      const atEnd = track.scrollLeft >= maxScroll - 1;

      const prev = this.querySelector("[data-cart-upsell-prev]");
      const next = this.querySelector("[data-cart-upsell-next]");

      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
    }
  }

  customElements.define(NAME, CartUpsell);
})();
